// Domů podle prototypu (data-screen-label="Domů"): pozdrav se jménem (vokativ) a datem, rychlé
// akce podle role, Oblíbené ★ jako dlaždice s kategorií (quick_links z osobního nastavení — připnutí hvězdičkou
// v menu nebo přes Upravit; dlaždice všech sekcí zadavatel 27. 9. 2026 zrušil) a dlaždice stavu serveru.
// Počítadla dne (dnešní zakázky, ke kontrole…) posílá server v Ranním přehledu (krok 4.10, `RanniPrehled.tsx`).
// Účet bez role vidí jen hlášku (08 §1 bod 6). Po přihlášení se jednou přistane podle role (§5.4).
// Obchodník a vedoucí obchodu mají v rychlých akcích „+ Naplánovat návštěvu“ a „+ Zapsat telefonát“ (krok 3.7).
// Provozní role bez zakládání zakázek mají „Požádat o svoz“ (krok 4.8) — drawer Požadavek na svoz rovnou z Domů.
// Ranní přehled a signály hlídače lhůt (krok 4.10, `RanniPrehled.tsx`): dlaždice s počty, agenda, souhrn dne a signály
// ze serveru (GET /ja/prehled) nad oblíbenými; `?datum=` v adrese jen pro testy (server bere dnešek v Praze).
// Co účet nesmí (chybí akce nebo sekce v /ja), se nevykreslí: rychlá akce, „Nastavení“ v Oblíbených, „Přepočítat teď“,
// „Vyřešeno“ u cizího signálu, odkaz do cizí sekce (`odkazy.ts`); řádek rychlých akcí bez akce taky ne (rozhodnutí 28. 9. 2026).
import type { Ja } from '@ecoser/shared';
import { Ikona } from '../shell/ikony';
import { useEffect, useState } from 'react';
import { Link, Navigate, useLocation } from 'react-router';
import { CESTA_NOVY_AUDIT } from '../api/audity';
import { naChybu } from '../api/klient';
import { useRanniPrehled } from '../api/prehled';
import { useZdravi } from '../api/zdravi';
import { useKontextObrazovky } from '../asistent/kontext-obrazovky';
import { AKCE_ZALOZIT_SVOZ } from '../api/svoz-pozadavky';
import { lina, NacitaniCasti } from '../komponenty/NacitaniCasti';
import { popisekRole } from '../prihlaseni/nazvy';
import type { PredlohaAktivity } from '../sekce/obchod/AktivitaFormular';
import { usePrihlaseni } from '../prihlaseni/stav-prihlaseni';
import { pristavaciSekce, sestavitMenu, vsechnyPolozky, type PolozkaMenu } from '../shell/menu';
import { useNastaveni } from '../shell/nastaveni-uzivatele';
import { oznacitPristani, pristaniCeka, zapamatovatPristani } from '../shell/pristani';
import { cestaSekce, fazeSekce, maObrazovku } from '../shell/sekce-cesty';
import { MojeKarta } from './MojeKarta';
import { RanniPrehledBlok } from './RanniPrehled';
import './Domu.css';

// Části jiných sekcí se stahují až při zobrazení (sekce mají vlastní chunky buildu — sekce/StrankaSekce.tsx;
// Domů je v hlavním balíku): formuláře z rychlých akcí (Obchod, Požadavky na svoz) a bloky ISŘ, které
// s dalšími kroky F11 rostou (čtečka dokumentů s podpisem, školení, údržba strojů).
const AktivitaFormular = lina(() =>
  import('../sekce/obchod/AktivitaFormular').then((m) => m.AktivitaFormular),
);
const PozadavekFormular = lina(() =>
  import('../sekce/svoz-pozadavky/PozadavekFormular').then((m) => m.PozadavekFormular),
);
const DokumentyKPodpisu = lina(() =>
  import('../sekce/iso/dokumenty/DokumentyKPodpisu').then((m) => m.DokumentyKPodpisu),
);
const MojeUdrzba = lina(() => import('../sekce/iso/zarizeni/MojeUdrzba').then((m) => m.MojeUdrzba));
// Moje hlášení k aplikaci — stav a odpověď správce (F12 12.5); blok se ukáže, jen když nějaké hlášení mám.
const MojeHlaseniDomu = lina(() => import('./MojeHlaseniDomu').then((m) => m.MojeHlaseniDomu));
const MojePripominkyDomu = lina(() =>
  import('./MojePripominkyDomu').then((m) => m.MojePripominkyDomu),
);

/** 5. pád křestního jména — převzato z prototypu (`vokativ`). */
export function vokativ(jmeno: string): string {
  if (!jmeno) return jmeno;
  const VYJIMKY: Record<string, string> = {
    Pavel: 'Pavle',
    David: 'Davide',
    Havel: 'Havle',
    Jiří: 'Jiří',
    Ondřej: 'Ondřeji',
    Matěj: 'Matěji',
    Mikuláš: 'Mikuláši',
    Aleš: 'Aleši',
    Tomáš: 'Tomáši',
    Lukáš: 'Lukáši',
    Jindřich: 'Jindřichu',
    Vojtěch: 'Vojtěchu',
    Oldřich: 'Oldřichu',
    Bedřich: 'Bedřichu',
  };
  const vyjimka = VYJIMKY[jmeno];
  if (vyjimka) return vyjimka;
  if (/a$/.test(jmeno)) return `${jmeno.slice(0, -1)}o`;
  if (/ek$/.test(jmeno)) return `${jmeno.slice(0, -2)}ku`;
  if (/[šžčřcj]$/.test(jmeno)) return `${jmeno}i`;
  if (/[kh]$/.test(jmeno) || /ch$/.test(jmeno)) return `${jmeno}u`;
  if (/[bcdfghjklmnpqrstvwxz]r$/i.test(jmeno)) return `${jmeno.slice(0, -1)}ře`;
  if (/[bdfglmnprstvz]$/.test(jmeno)) return `${jmeno}e`;
  return jmeno;
}

/** Pozdrav podle hodiny jako v prototypu. */
export function pozdrav(hodina: number): string {
  return hodina < 10 ? 'Dobré ráno' : hodina < 18 ? 'Dobrý den' : 'Dobrý večer';
}

/** Čas ve formátu 08:12 (docs/04: čas `08:12`). */
function formatCas(cas: number): string {
  return new Date(cas).toLocaleTimeString('cs-CZ', {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Europe/Prague',
  });
}

function DlazdiceStavuServeru() {
  const zdravi = useZdravi();

  let stitek: { trida: string; text: string };
  if (zdravi.isPending) {
    stitek = { trida: 'stitek--neutral', text: 'Zjišťuji…' };
  } else if (zdravi.isError) {
    stitek = { trida: 'stitek--danger', text: 'Nedostupný' };
  } else if (zdravi.data.stav === 'ok') {
    stitek = { trida: 'stitek--ok', text: 'V pořádku' };
  } else {
    stitek = { trida: 'stitek--warn', text: zdravi.data.stav };
  }

  const chyba = zdravi.isError ? naChybu(zdravi.error) : null;

  return (
    <section
      className="karta domu__karta"
      aria-labelledby="dlazdice-server-titul"
      aria-live="polite"
    >
      <div className="domu__karta-hlavicka">
        <h2 id="dlazdice-server-titul" className="domu__karta-titul">
          Stav serveru
        </h2>
        <span className={`stitek ${stitek.trida}`}>{stitek.text}</span>
      </div>

      <dl className="dlazdice__udaje">
        <div>
          <dt>Verze serveru</dt>
          <dd>{zdravi.data?.verze ?? '—'}</dd>
        </div>
        <div>
          <dt>Verze aplikace</dt>
          <dd>{__APP_VERZE__}</dd>
        </div>
        <div>
          <dt>Ověřeno</dt>
          <dd>{zdravi.dataUpdatedAt ? formatCas(zdravi.dataUpdatedAt) : '—'}</dd>
        </div>
      </dl>

      {chyba && (
        <div className="dlazdice__chyba">
          <p className="pole__chyba">{chyba.zprava}</p>
          <p className="pole__napoveda">Kód: {chyba.kod}</p>
          <button
            type="button"
            className="tlacitko tlacitko--sekundarni tlacitko--radek"
            onClick={() => void zdravi.refetch()}
            disabled={zdravi.isFetching}
          >
            Zkusit znovu
          </button>
        </div>
      )}
    </section>
  );
}

/** Role s rychlým zápisem aktivit na Domů (prototyp `homeAkce`, `jsemObchodnik` H:41 157; krok 3.7). */
const ROLE_OBCHODU = new Set(['obchodnik', 'vedouci_obchodu']);

/** Rychlé akce nad dlaždicemi (prototyp `homeAkce`) — jen podle sekcí a akcí z /ja. */
function RychleAkce({ ja }: { ja: Ja }) {
  const sekce = new Set(ja.sekce ?? []);
  const akce = new Set(ja.akce ?? []);
  const role = ja.role_efektivni || ja.role;
  const polni = role === 'ridic' || role === 'skladnik' || role === 'mlynar';
  const obchod = ROLE_OBCHODU.has(role ?? '') && akce.has('aktivita.zapsat');
  const [aktivita, setAktivita] = useState<{ predloha: PredlohaAktivity; klic: number } | null>(
    null,
  );
  const [svoz, setSvoz] = useState(false);
  const otevritAktivitu = (predloha: PredlohaAktivity) =>
    setAktivita((a) => ({ predloha, klic: (a?.klic ?? 0) + 1 }));
  // „+ Nová zakázka“ jen s právem i sekcí Zakázky; skladník zakládá příjem v Mém dni (nález SKL-F03).
  const novaZakazka = akce.has('zakazka.zalozit') && sekce.has('zakazky');
  const novyPrijem = akce.has('zakazka.zalozit') && !sekce.has('zakazky') && sekce.has('muj_den');
  const novyAudit =
    (role === 'auditor' || role === 'vedouci_auditor') && akce.has('audit.naplanovat');
  const mujDen = sekce.has('muj_den');
  const drceni = role === 'mlynar' && sekce.has('sklad_evidence');
  const pozadatOSvoz = akce.has(AKCE_ZALOZIT_SVOZ) && !novaZakazka;
  // Bez jediné akce se řádek nevykreslí (žádný prázdný obal vedle pozdravu).
  if (!novaZakazka && !novyPrijem && !novyAudit && !mujDen && !drceni && !pozadatOSvoz && !obchod)
    return null;

  return (
    <div className="domu__akce">
      {novaZakazka && (
        <Link to="/zakazky/nova" className="tlacitko tlacitko--primarni">
          + Nová zakázka
        </Link>
      )}
      {novyPrijem && (
        <Link
          to={`${cestaSekce('muj_den')}/novy-prijem`}
          className="tlacitko tlacitko--primarni"
          data-testid="domu-novy-prijem"
        >
          + Nový příjem
        </Link>
      )}
      {novyAudit && (
        <Link to={CESTA_NOVY_AUDIT} className="tlacitko tlacitko--primarni">
          + Nový audit
        </Link>
      )}
      {mujDen && (
        <Link
          to={cestaSekce('muj_den')}
          className={`tlacitko ${polni ? 'tlacitko--primarni' : 'tlacitko--sekundarni'}`}
        >
          Můj den
        </Link>
      )}
      {drceni && (
        <Link to={cestaSekce('sklad_evidence')} className="tlacitko tlacitko--primarni">
          Drcení a štítky
        </Link>
      )}
      {pozadatOSvoz && (
        <button
          type="button"
          className={`tlacitko ${role === 'skladnik' ? 'tlacitko--primarni' : 'tlacitko--sekundarni'}`}
          onClick={() => setSvoz(true)}
          data-testid="pozadat-o-svoz"
        >
          Požádat o svoz
        </button>
      )}
      {obchod && (
        <>
          <button
            type="button"
            className="tlacitko tlacitko--primarni"
            onClick={() => otevritAktivitu({ typ: 'navsteva' })}
          >
            + Naplánovat návštěvu
          </button>
          <button
            type="button"
            className="tlacitko tlacitko--sekundarni"
            onClick={() => otevritAktivitu({ typ: 'telefonat' })}
          >
            + Zapsat telefonát
          </button>
        </>
      )}
      {svoz && (
        <NacitaniCasti>
          <PozadavekFormular
            predloha={role === 'skladnik' ? { odkudZarizeniIcz: 'CZL00673' } : undefined}
            sekce="home"
            naZavrit={() => setSvoz(false)}
            naUlozeno={() => setSvoz(false)}
          />
        </NacitaniCasti>
      )}
      {aktivita && (
        <NacitaniCasti>
          <AktivitaFormular
            key={aktivita.klic}
            predloha={aktivita.predloha}
            sekce="home"
            naZavrit={() => setAktivita(null)}
            naUlozeno={(_a, navazna) => (navazna ? otevritAktivitu(navazna) : setAktivita(null))}
          />
        </NacitaniCasti>
      )}
    </div>
  );
}

/** Podtext dlaždice sekce: „otevřít“, u sekce bez obrazovky fáze, kdy přijde. */
function podtextSekce(klic: string): string {
  if (maObrazovku(klic)) return 'otevřít';
  const faze = fazeSekce(klic);
  return faze ? `připravuje se — ${faze.faze}` : 'připravuje se';
}

/**
 * Oblíbené ★ — sekce připnuté z osobního nastavení (quick_links) jako dlaždice s kategorií (styl prototypu
 * homeTiles). Zadavatel 27. 9. 2026: dlaždice **všech** sekcí z Domů pryč, „tenhle styl použij pro oblíbené —
 * když něco budu chtít, tak si to tam takhle připnu“. Připíná se hvězdičkou ★ v levém menu nebo přes Upravit.
 */
function Oblibene({ ja }: { ja: Ja }) {
  const { nastaveni, prepnoutOblibenou } = useNastaveni();
  const [upravuji, setUpravuji] = useState(false);
  const menu = sestavitMenu(ja);
  const kategorie = new Map<string, string>([
    ...menu.skupiny.flatMap((s) => s.polozky.map((p): [string, string] => [p.klic, s.nazev])),
    ...menu.ostatni.map((p): [string, string] => [p.klic, 'Ostatní']),
  ]);
  const polozky = vsechnyPolozky(menu).filter((p) => p.klic !== 'home');
  const oblibene = nastaveni.quick_links
    .map((klic) => polozky.find((p) => p.klic === klic))
    .filter((p): p is PolozkaMenu => Boolean(p));
  // Prototyp: Můj den se do oblíbených nenabízí (má vlastní tlačítko nahoře).
  const naVyber = polozky.filter((p) => p.klic !== 'muj_den');

  return (
    <section className="domu__oblibene" aria-labelledby="oblibene-titul">
      <div className="domu__karta-hlavicka">
        <h2 id="oblibene-titul" className="domu__karta-titul">
          Oblíbené
        </h2>
        {(ja.sekce ?? []).includes('nastaveni') && (
          <Link
            to={cestaSekce('nastaveni')}
            className="tlacitko tlacitko--sekundarni tlacitko--radek"
          >
            Nastavení
          </Link>
        )}
        <button
          type="button"
          className="domu__upravit"
          aria-pressed={upravuji}
          onClick={() => setUpravuji((u) => !u)}
        >
          {upravuji ? 'Hotovo' : 'Upravit'}
        </button>
      </div>

      {upravuji && (
        <div className="karta domu__vyber">
          <p className="pole__napoveda">
            Vyberte oblíbené — objeví se tady i nahoře v levém menu (hvězdička{' '}
            <Ikona id="star" velikost={12} /> je i u položek menu):
          </p>
          <div className="domu__chipy" role="group" aria-label="Oblíbené sekce">
            {naVyber.map((p) => {
              const zapnuto = nastaveni.quick_links.includes(p.klic);
              return (
                <button
                  key={p.klic}
                  type="button"
                  className={`chip${zapnuto ? ' chip--aktivni' : ''}`}
                  aria-pressed={zapnuto}
                  onClick={() => prepnoutOblibenou(p.klic)}
                >
                  {p.nazev}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {oblibene.length > 0 ? (
        <div className="domu__dlazdice">
          {oblibene.map((p) => (
            <Link
              key={p.klic}
              to={p.cesta}
              className="karta domu__dlazdice-polozka"
              data-sekce={p.klic}
            >
              <span className="domu__dlazdice-kategorie">{kategorie.get(p.klic) ?? ''}</span>
              <span className="domu__dlazdice-nazev">{p.nazev}</span>
              <span className="domu__dlazdice-podtext">{podtextSekce(p.klic)}</span>
            </Link>
          ))}
        </div>
      ) : (
        !upravuji && (
          <p className="domu__prazdne domu__prazdne--oblibene">
            {/* Jeden potomek — `.domu__prazdne` je flex a ikona by větu rozlámala na sloupce. */}
            <span>
              Zatím žádné oblíbené — připněte si sekci hvězdičkou <Ikona id="star" velikost={12} />{' '}
              v levém menu nebo přes Upravit.
            </span>
          </p>
        )
      )}
    </section>
  );
}

// Kniha jízd (W82 díl 2) se stahuje až na Domů — hlavní balík nesmí růst.
const KnihaJizdDomu = lina(() =>
  import('../sekce/kniha-jizd/KnihaJizdDomu').then((m) => m.KnihaJizdDomu),
);

/** Domů se při změně identity (začátek/konec náhledu za uživatele) sestaví znovu — přistání podle role. */
export function Domu() {
  const { ja } = usePrihlaseni();
  return <DomuVnitrek key={ja?.zamestnanec?.id ?? 'neznamy'} />;
}

function DomuVnitrek() {
  const { ja } = usePrihlaseni();
  const umisteni = useLocation();
  const idUzivatele = ja?.zamestnanec?.id ?? 'neznamy';
  // Přistání podle role jednou za relaci — jen když uživatel nešel na konkrétní cestu (bez query).
  // Inicializátor jen čte (React ho ve vývoji volá dvakrát); značka se zapíše až v efektu.
  const [presmerovatNa] = useState<string | null>(() => {
    if (!ja || umisteni.search) return null;
    const cil = pristavaciSekce(ja);
    if (cil === 'home') return null;
    return pristaniCeka(idUzivatele) ? cestaSekce(cil) : null;
  });
  useEffect(() => {
    if (!presmerovatNa) return;
    oznacitPristani(idUzivatele);
    // Při dalším spuštění na tomhle zařízení se kód cílové obrazovky stáhne hned při startu (main.tsx).
    zapamatovatPristani(presmerovatNa);
  }, [presmerovatNa, idUzivatele]);
  // Ranní přehled (4.10): `?datum=` jen pro testy; načítá se jen když Domů zůstane (ne při přistání jinam).
  const datumPrehledu = new URLSearchParams(umisteni.search).get('datum') ?? undefined;
  const prehled = useRanniPrehled(datumPrehledu, Boolean(ja) && !presmerovatNa);
  // Kontext pro panel asistenta: uživatel je na Domů (sekce `home`, 08 §2) — s datem přehledu a počtem
  // otevřených signálů, aby „co hoří“ mělo odkud brát čísla (docs/11 §7 bod 1).
  useKontextObrazovky(
    ja && !presmerovatNa
      ? {
          sekce: 'home',
          obrazovka: 'Domů',
          ...(prehled.data
            ? {
                filtr: {
                  datum: prehled.data.datum,
                  signaly_otevrene: prehled.data.signaly_celkem ?? prehled.data.signaly.length,
                  // Kancelář: kolik tabletů má neodeslané zápisy (05/136) a kolik lidí dnes chybí (05/242).
                  ...(prehled.data.neodeslane_zapisy?.length
                    ? { neodeslane_zapisy: prehled.data.neodeslane_zapisy.length }
                    : {}),
                  ...(prehled.data.nepritomni?.length
                    ? { nepritomni: prehled.data.nepritomni.length }
                    : {}),
                },
              }
            : {}),
        }
      : null,
  );

  if (!ja) return null;
  if (presmerovatNa) return <Navigate to={presmerovatNa} replace />;

  const zamestnanec = ja.zamestnanec;
  const krestni = zamestnanec?.jmeno ?? '';
  const role = popisekRole(ja.role_efektivni || ja.role);
  const ted = new Date();
  // Velké jen první písmeno („Pátek 2. října“) — měsíc se česky píše malým (nález DIS-S1).
  const den = ted.toLocaleDateString('cs-CZ', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    timeZone: 'Europe/Prague',
  });
  const datum = den.charAt(0).toUpperCase() + den.slice(1);

  return (
    <div className="domu" data-screen-label="Domů">
      <div className="domu__hlava">
        <div className="domu__pozdrav">
          <h1 className="domu__nadpis">
            {pozdrav(ted.getHours())}, {vokativ(krestni)}
          </h1>
          <p className="domu__datum">{datum}</p>
        </div>
        {role && <RychleAkce ja={ja} />}
      </div>

      {!role && (
        <div className="hlaska hlaska--warn" role="status">
          <strong>Účet nemá přiřazenou roli.</strong> Do přiřazení role je k dispozici jen Domů.
          Požádejte správce aplikace, aby vám v kartě zaměstnance nastavil roli.
        </div>
      )}

      <RanniPrehledBlok ja={ja} prehled={prehled} datum={datumPrehledu} />

      {/* Předpisy ISŘ k přečtení a podpisu, připomínka po přihlášení a čtečka ze zvonku — krok 11.1 (H:43438).
          Bez indikátoru načítání: blok se ukáže, jen když něco čeká. */}
      <NacitaniCasti tiche>
        <DokumentyKPodpisu ja={ja} />
      </NacitaniCasti>

      {/* Údržba strojů a kontroly zařízení, za které odpovídám — krok 11.6 (H:43430). */}
      <NacitaniCasti tiche>
        <MojeUdrzba ja={ja} />
      </NacitaniCasti>

      {/* Moje lhůty, převzetí k podpisu a (A J) úrazy k potvrzení — krok 10.1 (prototyp H:43429). */}
      <MojeKarta ja={ja} />

      {/* Kniha jízd služebního vozidla se soukromým užíváním — W82 díl 2 (vlastní chunk). */}
      <NacitaniCasti tiche>
        <KnihaJizdDomu ja={ja} />
      </NacitaniCasti>

      {/* Závady a náměty k aplikaci, které jsem nahlásil — stav a odpověď správce, krok 12.5 (H:43496). */}
      <NacitaniCasti tiche>
        <MojeHlaseniDomu ja={ja} />
      </NacitaniCasti>

      {/* Připomínky, které jsem napsal k dokumentům, a jestli je někdo vyřídil — 05/129. */}
      <NacitaniCasti tiche>
        <MojePripominkyDomu />
      </NacitaniCasti>

      <Oblibene ja={ja} />

      <div className="domu__sloupce">
        <DlazdiceStavuServeru />
      </div>
    </div>
  );
}
